<template>
    <div>
        <StepperLayoutMobile :steps="steps" :current-step="currentTab" />
        <br />
        <br />
        <component v-if="!loading" :is="currentTabComponent" @move-to-previous-step="goToPreviousStep"
            @move-to-next-step="handleNextClick" @select="handleDonationType" :formData="formData" :facilityId="facilityId"
            @final-submit="handleFinalSubmit" @donor-created="handleDonorCreated"
            :donorId="donorId"
            @navigate-to-vitals="goToVitals" @navigate-to-apheresis-screening="goToApheresisScreening">
        </component>

        <div v-else class="loader">
            Submitting...
        </div>
    </div>
</template>

<script>
import EducationalAlerts from "@/components/EducationalAlerts.vue";
import NewDonorEducationalPamphletMobile from "./forms/NewDonorEducationalPamphletMobile.vue";
import NewDonorApheresisPamphletMobile from "./forms/NewDonorApheresisPamphletMobile.vue";
import NewDonorInformationFormMobile from "./forms/NewDonorInformationFormMobile.vue";
import NewDonorQuestionnaireForm from "./forms/NewDonorQuestionnaireForm.vue";
import NewDonorDeclarationFormMobile from "./forms/NewDonorDeclarationFormMobile.vue";
import StepperLayoutMobile from "@/components/StepperLayoutMobile.vue";
import DonationType from "./forms/DonationType.vue";
import api from '@/services/api';
import SweetAlertService from '@/services/SweetAlertService';

export default {
    name: "NewDonor",
    components: {
        EducationalAlerts,
        StepperLayoutMobile,
        DonationType,
        NewDonorEducationalPamphletMobile,
        NewDonorApheresisPamphletMobile,
        NewDonorInformationFormMobile,
        NewDonorQuestionnaireForm,
        NewDonorDeclarationFormMobile,
    },
    props: {
        facilityId: {
            type: String,
            required: true,
        },
    },
data() {
        return {
            currentTab: 'DonationType',
            donorId: null,
            donationType: null,
            returningFromApheresis: false,
            formData: {
                educationalPamphlet: {},
                donorInformation: {},
                questionnaire: {},
                declaration: {}
            },
            loading: false,
            steps: [
                { id: 'DonationType', title: '1. Donation Type', subtitle: 'Select donation type', active: true },
                { id: 'NewDonorApheresisPamphletMobile', title: '2. Apheresis Pamphlet', subtitle: 'Read apheresis pamphlet', active: false },
                { id: 'NewDonorEducationalPamphletMobile', title: '2. Educational Pamphlet', subtitle: 'Read educational pamphlet', active: false },
                { id: 'NewDonorInformationFormMobile', title: '3. Donor Information', subtitle: 'Provide donor information', active: false },
                { id: 'NewDonorQuestionnaireForm', title: '4. Donor Questionnaire', subtitle: 'Answer the donor questionnaire', active: false },
                { id: 'NewDonorDeclarationFormMobile', title: '5. Declaration', subtitle: 'Fill in declaration & consent form', active: false },
            ]
        }
    },
    computed: {
        currentTabComponent() {
            switch (this.currentTab) {
                case "DonationType":
                    return DonationType;
                case "NewDonorEducationalPamphletMobile":
                    return NewDonorEducationalPamphletMobile;
                case "NewDonorApheresisPamphletMobile":
                    return NewDonorApheresisPamphletMobile;
                case "NewDonorInformationFormMobile":
                    return NewDonorInformationFormMobile;
                case "NewDonorQuestionnaireForm":
                    return NewDonorQuestionnaireForm;
                case "NewDonorDeclarationFormMobile":
                    return NewDonorDeclarationFormMobile;
                default:
                    return DonationType;
            }
        },
    },
    methods: {
        handleDonationType(type) {
            this.donationType = type;
        },
        handleNextClick() {
            if (this.currentTab === 'DonationType') {
                if (this.donationType === 'apheresis') {
                    this.setActiveStep('NewDonorApheresisPamphletMobile');
                } else if (this.donationType === 'normal') {
                    this.setActiveStep('NewDonorEducationalPamphletMobile');
                }
                return;
            }
            
            if (this.currentTab === 'NewDonorApheresisPamphletMobile') {
                this.returningFromApheresis = true;
                this.setActiveStep('NewDonorEducationalPamphletMobile');
                return;
            }
            
            if (this.currentTab === 'NewDonorEducationalPamphletMobile' && this.returningFromApheresis) {
                this.returningFromApheresis = false;
                this.setActiveStep('NewDonorInformationFormMobile');
                return;
            }
            
            const currentIndex = this.steps.findIndex(step => step.id === this.currentTab);
            if (currentIndex < this.steps.length - 1) {
                this.setActiveStep(this.steps[currentIndex + 1].id);
            }
        },
        handleDonorCreated(newDonorId) {
            this.donorId = newDonorId;
        },
        goToVitals(donorId) {
            this.$router.push({
                name: 'NewDonorVitalsForm',
                params: { donor_id: donorId }
            });
        },
        goToApheresisScreening(donorId) {
            this.$router.push({
                name: 'NewApheresisScreeningForm',
                params: { donor_id: donorId }
            });
        },
        setActiveStep(stepId) {
            this.steps.forEach(step => {
                step.active = step.id === stepId;
            });
            this.currentTab = stepId;
        },
        goToPreviousStep() {
            const currentIndex = this.steps.findIndex(step => step.id === this.currentTab);
            if (currentIndex > 0) {
                this.setActiveStep(this.steps[currentIndex - 1].id);
            }
        },
        goToNextStep() {
            const currentIndex = this.steps.findIndex(step => step.id === this.currentTab);
            if (currentIndex < this.steps.length - 1) {
                this.setActiveStep(this.steps[currentIndex + 1].id);
            }
        },
        async handleFinalSubmit(updatedFormData) {
            this.loading = true;

            // Update formData with the emitted data
            this.formData = { ...this.formData, ...updatedFormData };

            const user = {
                first_name: this.formData.donorInformation.first_name,
                last_name: this.formData.donorInformation.last_name,
                dob: this.formData.donorInformation.dob,
                gender: this.formData.donorInformation.gender,
                email: this.formData.donorInformation.email || undefined,
                ...(this.formData.donorInformation.phone_number && this.formData.donorInformation.phone_number !== '' ? { phone_number: this.formData.donorInformation.phone_number } : {}),
                blood_group_id: this.formData.donorInformation.blood_group_id,
                id_number: this.formData.donorInformation.id_number,
                physical_address: this.formData.donorInformation.physical_address,
                donor_status: this.formData.donorInformation.donor_status,
                donation_status: this.formData.donorInformation.donation_status,
                last_donation_date: this.formData.donorInformation.last_donation_date,
                enroll_as_donor: this.formData.donorInformation.enroll_as_donor,
                county_id: this.formData.donorInformation.county_id,
                terms_agreed: this.formData.donorInformation.terms_agreed,
                patient: this.formData.donorInformation.patient,
                ward: this.formData.donorInformation.ward,
                nok_names: this.formData.donorInformation.nok_names,
                nok_phone: this.formData.donorInformation.nok_phone,
                nok_relationship: this.formData.donorInformation.nok_relationship,
                password: "123456",
                password_confirmation: "123456",
            };

            const booking = {
                preferred_date: this.formData.donorInformation.preferred_date,
                preferred_time: this.formData.donorInformation.preferred_time,
            };

            const donorQuestionnaire = {
                feeling_healthy: this.formData.questionnaire?.feeling_healthy === "yes",
                take_flight: this.formData.questionnaire?.take_flight === "yes",
                minimum_weight: this.formData.questionnaire?.minimum_weight === "yes",
                active_allergy: this.formData.questionnaire?.active_allergy === "yes",
                severe_disease: this.formData.questionnaire?.severe_disease === "yes",
                had_alcohol: this.formData.questionnaire?.had_alcohol === "yes",
                had_drugs: this.formData.questionnaire?.had_drugs === "yes",
                had_tatoo: this.formData.questionnaire?.had_tatoo === "yes",
                had_acupuncture: this.formData.questionnaire?.had_acupuncture === "yes",
                hepatitis_rabies_vaccine: this.formData.questionnaire?.hepatitis_rabies_vaccine === "yes",
                had_blood_transfusion: this.formData.questionnaire?.had_blood_transfusion === "yes",
                yellow_fever_vaccine: this.formData.questionnaire?.yellow_fever_vaccine === "yes",
                covid_vaccine: this.formData.questionnaire?.covid_vaccine === "yes",
                taking_medication: this.formData.questionnaire?.taking_medication === "yes",
                donated_blood_component: this.formData.questionnaire?.donated_blood_component === "yes",
                dental_work: this.formData.questionnaire?.dental_work === "yes",
                unexplained_sickness: this.formData.questionnaire?.unexplained_sickness === "yes",
                relative_has_hepatitis: this.formData.questionnaire?.relative_has_hepatitis === "yes",
                had_body_piercing: this.formData.questionnaire?.had_body_piercing === "yes",
                immunization_vaccinations: this.formData.questionnaire?.immunization_vaccinations === "yes",
                pos_for_Hepatitis_C: this.formData.questionnaire?.pos_for_Hepatitis_C === "yes",
                had_surgery: this.formData.questionnaire?.had_surgery === "yes",
                infection_exposure: this.formData.questionnaire?.infection_exposure === "yes",
                medical_condition: this.formData.questionnaire?.medical_condition === "yes",
                positive_for_HIV: this.formData.questionnaire?.positive_for_HIV === "yes",
                bleeding_condition: this.formData.questionnaire?.bleeding_condition === "yes",
                rejected_as_donor: this.formData.questionnaire?.rejected_as_donor === "yes",
                had_antibiotics: this.formData.questionnaire?.had_antibiotics === "yes",
                read_material: this.formData.questionnaire?.read_material === "yes",
                had_malaria: this.formData.questionnaire?.had_malaria === "yes",
                had_body_graft: this.formData.questionnaire?.had_body_graft === "yes",
                contact_with_a_bleeder: this.formData.questionnaire?.contact_with_a_bleeder === "yes",
                needle_injury_accident: this.formData.questionnaire?.needle_injury_accident === "yes",
                treated_sti: this.formData.questionnaire?.treated_sti === "yes",
                been_in_prison: this.formData.questionnaire?.been_in_prison === "yes",
                had_tb: this.formData.questionnaire?.had_tb === "yes",
                had_organ_transplant: this.formData.questionnaire?.had_organ_transplant === "yes",
                spend_time_in_uk: this.formData.questionnaire?.spend_time_in_uk === "yes",
                drugs_injection: this.formData.questionnaire?.drugs_injection === "yes",
                money_for_sex: this.formData.questionnaire?.money_for_sex === "yes",
                had_heart_condition: this.formData.questionnaire?.had_heart_condition === "yes",
                has_periods: this.formData.questionnaire?.has_periods === "yes",
                is_breastfeeding: this.formData.questionnaire?.is_breastfeeding === "yes",
                is_pregnant: this.formData.questionnaire?.is_pregnant === "yes",
            };

            const declaration = {
                read_educational_material: this.formData.declaration.read_educational_material,
                consent_checkbox: this.formData.declaration.consent_checkbox,
                blood_testing_consent: this.formData.declaration.blood_testing_consent,
                terms_agreed: this.formData.declaration.terms_agreed,
                consent_form: this.formData.declaration.consent_form,
                signature_url: this.formData.declaration.signature_url, // This should now include the signature_url
            };

            const payload = {
                donor_questionnaire: donorQuestionnaire,
                declaration: declaration,
                booking: booking,
                user: user,
                facility_id: this.facilityId,
            };

            // Call your API with the payload
            try {
                await this.submitToApi(payload);
            } catch (error) {
                console.error("Error submitting the form:", error);
            } finally {
                this.loading = false; // Stop loading
            }
        },
        async submitToApi(payload) {
            try {
                const response = await api.createDonor(payload);
                console.log('Submission successful:', response);
                await SweetAlertService.showSuccess('Success!', 'Donor information saved successfully!');

                this.$router.push({ name: 'GratitudeMessage' });
            } catch (error) {
                console.error('Error submitting the form:', error);
                await SweetAlertService.showError('Submission Error', error.message || 'An error occurred while saving the donor information.');
            }
        }
    },
};
</script>

<style scoped>
.loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    /* Full-height loader */
    font-size: 24px;
    /* Adjust size */
    color: #F8951E;
    /* Loader color */
}
</style>